import { localize } from "client_legacy/ndaw/culture";
import {
	InputValidationResult,
	InputValidationResultKind,
	InvalidInputValidationResult,
	isInvalid,
	ValidInputValidationResult
} from "./input";
import { ExecuteValidationParams } from "./validationRunner";
import { ModelPath } from "./path";
import { isNotNil } from "client/Misc/utils";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { NormalizedSet } from "client_legacy/ndaw/normalizedSet";

export type ValidationRuleState = {
	readonly isActivated: boolean;
	readonly result: InputValidationResult;
};

export type NodeValidationState = {
	readonly stablePath: string;
	readonly modelPath: ModelPath;
	readonly clientResult: InputValidationResult;
	readonly serverResult: InputValidationResult;
	readonly conversionRuleState: ValidationRuleState;
	readonly ruleStates: readonly ValidationRuleState[];
	readonly controlValidation: InputValidationResult | undefined;
};

export function getDefaultNodeState(
	stablePath: string,
	modelPath: ModelPath,
	ruleCount: number
): NodeValidationState {
	const defaultValidationResult: InputValidationResult = { kind: InputValidationResultKind.Valid };

	const defaultRuleState: ValidationRuleState = {
		isActivated: false,
		result: defaultValidationResult
	};

	const ruleStates: ValidationRuleState[] = range(ruleCount)
		.map(_ => defaultRuleState);

	return {
		stablePath,
		modelPath,
		clientResult: defaultValidationResult,
		serverResult: defaultValidationResult,
		controlValidation: defaultValidationResult,
		conversionRuleState: defaultRuleState,
		ruleStates
	};
}

export type RootValidationState = {
	readonly inputStates: readonly NodeValidationState[];
	readonly triggers: readonly ExecuteValidationParams[];
};

export enum InputValidationResultErrorSource {
	ClientError = 0,
	ServerError = 1,
	ControlError = 2,
	ChildError = 3,
}

export type CombinedInputValidationResult = ValidInputValidationResult | (InvalidInputValidationResult & {
	errorSource: InputValidationResultErrorSource;
});

export class ModelPathMap<TValue> extends NormalizedMap<ModelPath, TValue> {
	constructor(iterable?: Iterable<readonly [ModelPath, TValue]>) {
		super(x => x.toString(), iterable);
	}
}

export class ModelPathSet extends NormalizedSet<ModelPath> {
	constructor(iterable?: Iterable<ModelPath>) {
		super(x => x.toString(), iterable);
	}
}

export function getCombinedState(
	nodeStates: readonly NodeValidationState[],
	modelPaths: ModelPath | (ReadonlySet<ModelPath>)
): CombinedInputValidationResult {
	const relevantStates: NodeValidationState[] = nodeStates.filter(
		s => modelPaths instanceof ModelPath
			? modelPaths.equals(s.modelPath)
			: modelPaths.has(s.modelPath)
	);

	const controlError: InvalidInputValidationResult | undefined = relevantStates
		.map(s => s.controlValidation)
		.find(v => isNotNil(v) && isInvalid(v));

	if (controlError) {
		return {
			...controlError,
			errorSource: InputValidationResultErrorSource.ControlError
		};
	}

	const clientError: InvalidInputValidationResult | undefined = relevantStates
		.map(s => s.clientResult)
		.find(isInvalid);

	if (clientError) {
		return {
			...clientError,
			errorSource: InputValidationResultErrorSource.ClientError
		};
	}

	const serverError: InvalidInputValidationResult | undefined = relevantStates
		.map(s => s.serverResult)
		.find(isInvalid);

	if (serverError) {
		return {
			...serverError,
			errorSource: InputValidationResultErrorSource.ServerError
		};
	}

	const nodesWithError: NodeValidationState[] = nodeStates.filter(n => isInvalid(n.clientResult)
		|| isInvalid(n.serverResult)
		|| (isNotNil(n.controlValidation) && isInvalid(n.controlValidation))
	);

	const hasChildError: boolean = relevantStates.some(
		r => nodesWithError.some(
			c => c.modelPath.isDescendentOf(r.modelPath)
		)
	);

	if (hasChildError) {
		return {
			kind: InputValidationResultKind.Invalid,
			errorMessage: localize("_NotAllFieldsAreValid"),
			errorSource: InputValidationResultErrorSource.ChildError
		};
	}

	return {
		kind: InputValidationResultKind.Valid
	};
}

export type ImperativeValidationState = {
	modelPath: ModelPath;
	serverResult?: InputValidationResult;
	controlValidation?: InputValidationResult;
};

export function hasAnyValidationResult(state: ImperativeValidationState): boolean {
	return isNotNil(state.controlValidation)
		|| isNotNil(state.serverResult);
}

const rootPathForResultsDisconnectedFromValidationStructure = "6cbff7ca-b311-4256-84e7-3e1165adb542";

export function getStablePathForResultDisconnectedFromValidatorStructure(
	modelPath: ModelPath
): string {
	return `${rootPathForResultsDisconnectedFromValidationStructure}.${modelPath.toString()}`;
}

export function isDisconnectedFromValidationStructure(stablePath: string) {
	return stablePath.startsWith(rootPathForResultsDisconnectedFromValidationStructure);
}